import {format, differenceInSeconds, formatDuration, intervalToDuration, type Interval, isBefore} from 'date-fns';
import {de} from 'date-fns/locale/de'
import type {FormatDistanceToken} from "date-fns/locale";
import type {DurationUnit} from "date-fns";

export const TAG_NAME = 'ft9-benefit-countdown-v1';
export const EXPIRATION_DATE_ATTRIBUTE = 'expiration-date';
export const UPDATE_INTERVAL_ATTRIBUTE = 'update-interval';
export const FORMAT_ATTRIBUTE = 'format';

const ONE_MINUTE = 60;
const ONE_HOUR = ONE_MINUTE * 60;
const ONE_DAY = ONE_HOUR * 24;
const TWO_WEEKS = ONE_DAY * 7 * 2;

export type ScarcityType = 'expired' | 'upTo24Hours' | 'tomorrow' | 'upToTwoWeeks' | 'moreThanTwoWeeks';

export class CountdownComponent extends HTMLElement {
    private root: ShadowRoot;
    private expirationDate: Date = new Date(0);
    private updateIntervalInMilliseconds: number = 500;

    private variant?: ScarcityType;

    private slotMap: Map<ScarcityType, HTMLSlotElement> = new Map();

    private intervalId?: ReturnType<typeof setInterval>;

    constructor() {
        super();
        this.root = this.attachShadow({mode: 'open'});
    }

    connectedCallback() {
        const expirationDate = this.getAttribute(EXPIRATION_DATE_ATTRIBUTE);
        if (!expirationDate) {
            return;
        }
        this.expirationDate = new Date(expirationDate);

        const updateIntervalAttributeValue = this.getAttribute(UPDATE_INTERVAL_ATTRIBUTE);
        if (updateIntervalAttributeValue) {
            this.updateIntervalInMilliseconds = parseInt(updateIntervalAttributeValue);
        }

        this.render();

        this.initCountdown();

        this.intervalId = setInterval(() => {
            this.updateCountdown()
        }, this.updateIntervalInMilliseconds);
    }

    private render() {
        this.root.innerHTML = `
          <slot name="expired" style="display: none"></slot>
          <slot name="upTo24Hours" style="display: none"></slot>
          <slot name="tomorrow" style="display: none"></slot>
          <slot name="upToTwoWeeks" style="display: none"></slot>
          <slot name="moreThanTwoWeeks" style="display: none"></slot>`;

        this.slotMap.set('expired', this.root.querySelector('slot[name="expired"]') as HTMLSlotElement);
        this.slotMap.set('upTo24Hours', this.root.querySelector('slot[name="upTo24Hours"]') as HTMLSlotElement);
        this.slotMap.set('tomorrow', this.root.querySelector('slot[name="tomorrow"]') as HTMLSlotElement);
        this.slotMap.set('upToTwoWeeks', this.root.querySelector('slot[name="upToTwoWeeks"]') as HTMLSlotElement);
        this.slotMap.set('moreThanTwoWeeks', this.root.querySelector('slot[name="moreThanTwoWeeks"]') as HTMLSlotElement);
    }

    private initCountdown() {
        /*                                                                                                                             */
        /*                                                                                                                                                          */
        /*                                                                                                                                              */

        let slotCounter = 0;
        let isInitialized = false; /*                                                                                   */

        const onSlotChange = () => {
            slotCounter++;
            if (slotCounter === this.slotMap.size - 1 && !isInitialized) {
                this.updateCountdown();
                this.slotMap.forEach((slot) => {
                    slot.removeEventListener('slotchange', onSlotChange);
                })
                isInitialized = true;
            }
        };

        this.slotMap.forEach((slot) => {
            slot.addEventListener('slotchange', onSlotChange)
        })
    }

    private updateCountdown() {
        const interval = {start: new Date(), end: this.expirationDate};
        this.updateVariant(interval);
        this.displayVariant(interval);
    }

    private updateVariant(interval: Interval) {
        const diff = differenceInSeconds(interval.end, interval.start);
        if (diff <= 0) {
            this.variant = 'expired';
            if (this.intervalId) {
                clearInterval(this.intervalId);
            }
        } else if (diff <= ONE_DAY) {
            this.variant = 'upTo24Hours';
        } else if (this.isTomorrow(this.expirationDate)) {
            this.variant = 'tomorrow';
        } else if (diff <= TWO_WEEKS) {
            this.variant = 'upToTwoWeeks';
        } else {
            this.variant = 'moreThanTwoWeeks';
        }
    }

    static updateTextContent(child: Element, newContent: string): void {
        if (child.textContent === newContent) return;
        child.textContent = newContent;   
    }

    private displayVariant(interval: Interval) {
        this.slotMap.forEach((slot) => {
            slot.style.display = slot.name === this.variant ? '' : 'none';
        });
        const assignedElement = this.slotMap.get(this.variant!)!.assignedElements()[0];

        if (!assignedElement) {
            return
        }

        [...assignedElement.children].forEach((child) => {
            const formatAttributeValue = child.getAttribute(FORMAT_ATTRIBUTE)!;

            if (!formatAttributeValue) {
                return
            }

            if (formatAttributeValue.includes("timeDiff")) {
                return CountdownComponent.updateTextContent(child, this.getFormattedCountdown(interval, ["hours", "minutes", "seconds"]));
            }

            if (formatAttributeValue.includes("duration")) {
                return CountdownComponent.updateTextContent(child, this.getFormattedCountdown(interval, ["days", "hours", "minutes"]));
            }

            CountdownComponent.updateTextContent(child, format(this.expirationDate, child.getAttribute(FORMAT_ATTRIBUTE)!, {locale: de}));
        });
    }

    private getFormattedCountdown(interval: Interval, units: DurationUnit[]) {

        if (differenceInSeconds(interval.end, interval.start) < 60 && !units.includes("seconds")) {
            return '0m'
        }
        const formatDistanceLocale = { xDays: '{{count}}d', xMinutes: '{{count}}m', xHours: '{{count}}h', xSeconds: '{{count}}s' } as Record<FormatDistanceToken, string>
        const shortLocale = { formatDistance: (token: FormatDistanceToken, count: number) => formatDistanceLocale[token].replace('{{count}}', `${count}`) }

        return formatDuration(intervalToDuration(interval), {zero: false, format: units , locale: shortLocale});
    }

    private isTomorrow(date: Date) {
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);

        return date.toDateString() === tomorrow.toDateString();
    }
}

customElements.define(TAG_NAME, CountdownComponent);